import * as React from 'react'
import { Box, Form, Accordion, Checkbox } from 'cs-kevlar-react'
import { Formik } from 'formik'
import {
  EditModalActionBar,
  FormikTextField,
  FormikSelect,
  ConfirmDeleteModal,
} from 'components'
import { Grid } from 'cs-kevlar-react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  Guid,
  CustomerCertificateViewModel,
  CertificateRevocationStatus,
  CustomerInterfaceModel,
  InterfaceHealthColor,
} from '../../api'
import { InterfaceRegistration } from './InterfaceRegistration'
import {
  useConnectorApi,
  usePageNotifications,
  useCurrentCustomer,
  useLogger,
} from 'hooks'
import { CustomerInterface } from 'contexts/CurrentCustomerContext'
import * as Yup from 'yup'
import { getAllInterfaceLogLevels } from 'utils'
import { CertificateHistory } from '../CertificateHistory'
import clsx from 'clsx'

interface EditConnectorProps {
  connector: CustomerInterface
  onClose: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    formFields: {
      // setting the spacing on the Grid adds to the width and subtracts from the margin, but not enough
      margin: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    gridItem: {
      width: '100%',
    },
    accordion: {
      '& .MuiAccordionDetails-root': {
        flexDirection: 'column',
      },
    },
    certStatus: {
      marginTop: theme.spacing(8),
    },
  }),
)

const EditConnectorFormSchema = Yup.object().shape({
  name: Yup.string().required('The Name field is required.'),
  notes: Yup.string(),
})

const LogLevelOptions = getAllInterfaceLogLevels()
const LogTargetOptions = [
  {
    key: 'AWS',
    label: 'AWS ELK',
    value: true,
  },
  {
    key: 'Local',
    label: 'Local ELK',
    value: false,
  },
]

type NullableCustomerCertificateViewModel = CustomerCertificateViewModel | null

export const EditConnector: React.FunctionComponent<EditConnectorProps> = ({
  connector,
  onClose,
}) => {
  const classes = useStyles({})
  const { logError } = useLogger()

  if (!connector.customerAgencyId) return null

  const { dispatch: dispatchCustomerAction } = useCurrentCustomer(
    connector.customerAgencyId,
  )

  const { editConnector, addConnector, deleteConnector } = useConnectorApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(
    false,
  )

  const isAdding = connector.id === 'NEW'
  const isEditing = connector.id !== 'NEW'

  const onSubmit = (values: CustomerInterface, formikActions: any) => {
    if (isEditing && connector.customerAgencyId)
      onEdit(
        connector.customerAgencyId,
        connector.healthState,
        values,
        onClose,
        formikActions,
      )
    if (isAdding) onAdd(values, onClose, formikActions)
  }

  const onEdit = (
    customerAgencyId: Guid,
    healthState: InterfaceHealthColor | undefined,
    values: CustomerInterface,
    onComplete: () => void,
    formikActions: any,
  ) => {
    editConnector(customerAgencyId, values.id, values)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const newConnector: CustomerInterface = {
            ...response.data.data,
            id: response.data.data.customerInterfaceId as string,
            createdOn: new Date().toUTCString(),
            healthState: healthState,
            configurationOverrides: {},
            importConfiguration: null,
          }

          dispatchCustomerAction({
            type: 'EditConnector',
            payload: newConnector,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Connector updated successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error editing the connector',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error editing the connector',
        })
      })
  }

  const onAdd = (
    values: CustomerInterface,
    onComplete: () => void,
    formikActions: any,
  ) => {
    // remove the NEW id and licensedAgencyId so that the server will generate a new GUID
    const newConnector = Object.keys(values)
      .filter((key) => key !== 'id' && key !== 'customerInterfaceId')
      .reduce((accum, current) => {
        const value = (values as any)[current]
        return {
          ...accum,
          [current]: value,
        } as CustomerInterfaceModel
      }, {} as CustomerInterfaceModel)

    addConnector(connector.customerAgencyId as string, newConnector)
      .then((response) => {
        if (!response) return

        formikActions.setSubmitting(false)

        if (response.status === 200 && response.data.isSuccess) {
          const newConnector: CustomerInterface = {
            ...response.data.data,
            id: response.data.data.customerInterfaceId as string,
            createdOn: new Date().toUTCString(),
            configurationOverrides: {},
            importConfiguration: null,
          }

          dispatchCustomerAction({
            type: 'AddConnector',
            payload: newConnector,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Connector added successfully.',
          })

          onComplete()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error adding the connector',
          })
        }
      })
      .catch((error) => {
        formikActions.setSubmitting(false)

        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error adding the connector',
        })
      })
  }

  const onDelete = () => {
    deleteConnector(connector.customerAgencyId as string, connector.id)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          dispatchCustomerAction({
            type: 'DeleteConnector',
            payload: connector.id,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Connector deleted successfully.',
          })

          setIsConfirmingDelete(false)
          onClose()
        } else {
          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error deleting the connector',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error deleting the connector',
        })
      })
  }

  const connectorWithDefaults = {
    ...connector,
  }

  if (connectorWithDefaults.notes === null) {
    connectorWithDefaults.notes = ''
  }

  // we don't want to submit this back to the server
  delete connectorWithDefaults.healthState

  if (
    typeof connectorWithDefaults.certificateRevocationStatus === 'undefined' ||
    connectorWithDefaults.certificateRevocationStatus === null
  ) {
    connectorWithDefaults.certificateRevocationStatus =
      CertificateRevocationStatus.Ok
  }

  const certificateHistory: NullableCustomerCertificateViewModel[] =
    connectorWithDefaults.certificateHistory &&
    connectorWithDefaults.certificateHistory.length > 0
      ? connectorWithDefaults.certificateHistory
      : [null]

  return (
    <React.Fragment>
      <Formik
        initialValues={connectorWithDefaults}
        validationSchema={EditConnectorFormSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Box className={classes.formFieldContainer}>
              <Accordion
                title="Details"
                initiallyExpanded
                id="EditConnector::Details"
                className={classes.accordion}
              >
                <Grid
                  container
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                  className={classes.formFields}
                >
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName="name"
                      label="Name"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item className={classes.gridItem}>
                    <FormikTextField
                      formikProps={formikProps}
                      fieldName="notes"
                      label="Notes"
                      fullWidth
                      multiline
                      rows={5}
                    />
                  </Grid>
                  {isEditing && (
                    <Grid item className={classes.gridItem}>
                      <InterfaceRegistration
                        registration={connector.interfaceRegistration}
                      />
                    </Grid>
                  )}
                </Grid>
              </Accordion>
            </Box>
            <Box className={classes.spacer} />
            <EditModalActionBar
              canDelete={isEditing}
              onDelete={() => setIsConfirmingDelete(true)}
              onCancel={onClose}
              formikProps={formikProps}
              saveText="Save"
              deleteText="Delete Connector"
            />
          </Form>
        )}
      </Formik>
      {isEditing && (
        <ConfirmDeleteModal
          onCancel={() => setIsConfirmingDelete(false)}
          onConfirm={onDelete}
          open={isConfirmingDelete}
          line1Text="Please confirm you wish to delete this connector.  This action cannot be reversed."
          line2Text={`Connector: ${connector.name}`}
          inputLabelText="Please enter DELETE to confirm deletion of this connector."
        />
      )}
    </React.Fragment>
  );
}
